import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSound } from '../../utils/sound/SoundProvider';
import useGameBase from '../../hooks/useGameBase';

function SurveySaysGame({ roomId, gameState, gamePlayers = [], user = {}, socket, onGameAction }) {
  const sound = useSound();
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [guess, setGuess] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [scratchingAnswers, setScratchingAnswers] = useState({});
  const inputRef = useRef(null);

  const players = Array.isArray(gameState?.players) ? gameState.players : gamePlayers;
  const myId = user?.socketId || user?.userId || null;
  const playerProgress = useMemo(() => gameState?.playerProgress || {}, [gameState?.playerProgress]);
  // Use per-player revealed answers in 'first to find all' mode, otherwise use global revealedAnswers
  const gameMode = gameState?.gameMode || 'firstToFindAll';
  const revealedAnswers = useMemo(() => {
    if (gameMode === 'firstToFindAll' && !gameState?.finished && myId && gameState?.perPlayerRevealedAnswers?.[myId]) {
      return gameState.perPlayerRevealedAnswers[myId];
    } else if (Array.isArray(gameState?.revealedAnswers)) {
      return gameState.revealedAnswers;
    }
    return [];
  }, [gameMode, gameState?.finished, myId, gameState?.perPlayerRevealedAnswers, gameState?.revealedAnswers]);
  const totalAnswers = Number(gameState?.totalAnswers || revealedAnswers.length || 0);
  const finished = !!gameState?.finished;
  const result = gameState?.result || null;
  const roundEndsAt = Number(gameState?.roundEndsAt || 0);
  const timeLimit = Number(gameState?.timeLimit || 0);
  const giveUpRequest = gameState?.giveUpRequest || null;
  const myProgress = myId ? playerProgress[myId] || { foundIndexes: [], incorrectCount: 0 } : { foundIndexes: [], incorrectCount: 0 };
  const myLastSubmission = myProgress?.lastSubmission || null;
  const myApprovedGiveUp = !!giveUpRequest?.approvals?.[myId];
  const requestedByMe = !!myId && giveUpRequest?.requestedBy === myId;
  const requesterName = players.find((player) => (player.socketId || player.userId || player.id) === giveUpRequest?.requestedBy)?.username || 'A player';

  const playerRows = useMemo(() => {
    return players.map((player) => {
      const playerId = player.socketId || player.userId || player.id;
      const progress = playerProgress[playerId] || { foundIndexes: [], incorrectCount: 0 };
      return {
        id: playerId,
        username: player.username || 'Player',
        foundIndexes: Array.isArray(progress.foundIndexes) ? progress.foundIndexes : [],
        incorrectCount: Number(progress.incorrectCount || 0),
        score: Number(gameState?.scores?.[playerId] || player.score || 0),
        isWinner: Array.isArray(gameState?.winners) && gameState.winners.includes(playerId),
        isMe: !!myId && myId === playerId,
      };
    });
  }, [gameState?.scores, gameState?.winners, myId, playerProgress, players]);

  const shouldShowSharedAnswers = gameMode === 'firstToFindEachAnswer' || finished;

  const visibleAnswerCount = useMemo(() => revealedAnswers.filter(Boolean).length, [revealedAnswers]);

  useEffect(() => {
    if (!roundEndsAt || finished) {
      setTimeLeft(null);
      return undefined;
    }

    const update = () => {
      setTimeLeft(Math.max(0, Math.ceil((roundEndsAt - Date.now()) / 1000)));
    };

    update();
    const intervalId = setInterval(update, 250);
    return () => clearInterval(intervalId);
  }, [finished, roundEndsAt]);

  useEffect(() => {
    if (!finished && inputRef.current) {
      inputRef.current.focus();
    }
  }, [finished]);

  const dispatchAction = async (action) => {
    if (typeof onGameAction === 'function') {
      return onGameAction(action);
    }
    return submitAction(action);
  };

  const prevProgressRef = useRef(myProgress.foundIndexes.length);
  const prevSubmissionRef = useRef(null);
  const previousRevealedAnswersRef = useRef(revealedAnswers);
  const scratchTimeoutsRef = useRef({});
  const hasSeenBoardRef = useRef(false);

  useEffect(() => {
    // Keep the input tidy after a successful reveal, but don't clear feedback here.
    if (myProgress.foundIndexes.length > prevProgressRef.current) {
      setGuess('');
    }
    prevProgressRef.current = myProgress.foundIndexes.length;
  }, [myProgress.foundIndexes.length]);

  // Always clear feedback when a new game or round starts (prevents stale messages)
  useEffect(() => {
    setFeedback(null);
  }, [gameState?.roundId, gameState?.gameId, gameState?.finished]);
  
  useEffect(() => {
    if (!myLastSubmission) return;

    const submissionKey = JSON.stringify({
      status: myLastSubmission.status,
      answerIndex: myLastSubmission.answerIndex,
      answer: myLastSubmission.answer,
      guess: myLastSubmission.guess,
      foundBy: myLastSubmission.foundBy,
    });

    if (submissionKey === prevSubmissionRef.current) {
      return;
    }

    prevSubmissionRef.current = submissionKey;

    if (myLastSubmission.status === 'correct') {
      setFeedback({ tone: 'success', text: `Correct: ${myLastSubmission.answer}` });
      setGuess('');
      if (sound) sound.play('ui:positive');
      const timeout = setTimeout(() => setFeedback(null), 1500);
      return () => clearTimeout(timeout);
    }


    if (myLastSubmission.status === 'duplicate') {
      setFeedback({ tone: 'warn', text: 'You already found that answer.' });
      return;
    }

    if (myLastSubmission.status === 'taken') {
      setFeedback({
        tone: 'warn',
        text: `${myLastSubmission.answer} has already been claimed by ${gameState?.revealedAnswers?.[myLastSubmission.answerIndex]?.foundByName || 'another player'}.`,
      });
      return;
    }

    if (myLastSubmission.status === 'close') {
      setFeedback({ tone: 'warn', text: 'Close!!' });
      if (sound) sound.play('ui:negative');
      const timeout = setTimeout(() => setFeedback(null), 1000);
      return () => clearTimeout(timeout);
    }

    if (myLastSubmission.status === 'incorrect') {
      setFeedback({ tone: 'error', text: 'No match for that guess.' });
      if (sound) sound.play('ui:negative');
      const timeout = setTimeout(() => setFeedback(null), 1000);
      return () => clearTimeout(timeout);
    }
  }, [gameState?.revealedAnswers, myLastSubmission, sound]);

  useEffect(() => {
    if (!hasSeenBoardRef.current) {
      hasSeenBoardRef.current = true;
      previousRevealedAnswersRef.current = revealedAnswers;
      return undefined;
    }

    const newlyRevealedIndexes = [];
    for (let index = 0; index < revealedAnswers.length; index += 1) {
      if (revealedAnswers[index] && !previousRevealedAnswersRef.current?.[index]) {
        newlyRevealedIndexes.push(index);
      }
    }

    if (newlyRevealedIndexes.length > 0) {
      setScratchingAnswers((current) => {
        const next = { ...current };
        newlyRevealedIndexes.forEach((index) => {
          next[index] = true;
        });
        return next;
      });

      newlyRevealedIndexes.forEach((index) => {
        if (scratchTimeoutsRef.current[index]) {
          clearTimeout(scratchTimeoutsRef.current[index]);
        }
        scratchTimeoutsRef.current[index] = setTimeout(() => {
          setScratchingAnswers((current) => {
            if (!current[index]) return current;
            const next = { ...current };
            delete next[index];
            return next;
          });
          delete scratchTimeoutsRef.current[index];
        }, 850);
      });
    }

    previousRevealedAnswersRef.current = revealedAnswers;

    return undefined;
  }, [revealedAnswers]);

  useEffect(() => {
    const timeoutMap = scratchTimeoutsRef.current;
    return () => {
      Object.values(timeoutMap).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = guess.trim();
    if (!trimmed || finished) return;

    const response = await dispatchAction({ type: 'submit-answer', answer: trimmed });

    // SurveySays gets authoritative submit feedback from playerProgress.lastSubmission.
    // Ignore optimistic placeholders and wait for the next game state update.
    if (response?.ok && response.optimistic) {
      return;
    }

    if (response?.error || response?.ok === false || response?.status === 'error') {
      setFeedback({ tone: 'error', text: 'That guess could not be submitted.' });
    }
  };

  const handleGiveUpRequest = async () => {
    const response = await dispatchAction({ type: 'request_give_up' });
    if (response?.requested || response?.status === 'ended') {
      setFeedback(null);
    }
  };

  const handleGiveUpResponse = async (approve) => {
    await dispatchAction({ type: 'respond_give_up', approve });
  };

  const bannerText = useMemo(() => {
    const winnerText = result?.winnerNames || 'The round';
    const endedReason = gameState?.endedReason;

    if (!finished) return null;
    if (endedReason === 'timeout') return `${winnerText} ${result?.outcome === 'tie' ? 'are tied when time expired.' : 'lead when time expired.'}`;
    if (endedReason === 'give_up') return `${winnerText} ${result?.outcome === 'tie' ? 'are tied when the round ended early.' : 'lead when the round ended early.'}`;
    if (endedReason === 'all_answers_claimed') return `${winnerText} ${result?.outcome === 'tie' ? 'finished tied after every answer was claimed.' : 'won after every answer was claimed.'}`;
    return `${winnerText} ${result?.outcome === 'tie' ? 'finished tied.' : 'won the round.'}`;
  }, [finished, gameState?.endedReason, result]);

  if (!gameState) {
    return (
      <div className="rounded-3xl bg-white p-6 text-gray-900 shadow-xl dark:bg-slate-900 dark:text-gray-100">
        Loading Survey Says...
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-lg p-3 text-gray-900 dark:text-gray-100 sm:p-4">
      <style>{`
        @keyframes surveysaysScratchReveal {
          0% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
          55% {
            opacity: 1;
            transform: translateX(18%) scale(1.01);
          }
          100% {
            opacity: 0;
            transform: translateX(115%) scale(1.03);
          }
        }
        @media (max-width: 950px) {
          .surveysays-responsive-grid {
            display: flex !important;
            flex-direction: column !important;
          }
        }
      `}</style>
      <div className="overflow-hidden rounded-[28px] border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-white to-amber-50 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-cyan-900/60 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/80">
        <div className="p-4 sm:p-6">

          {finished && (
            <div className="mb-4 rounded-2xl border border-emerald-300 bg-emerald-100/90 px-4 py-3 text-sm font-semibold text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-100">
              {bannerText || 'The round is complete.'}
            </div>
          )}

          <div className="flex flex-col gap-5 w-full max-w-full">
            <div className="space-y-5 min-w-0">
              <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/85">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="mt-3 text-xl font-semibold text-slate-900 dark:text-white sm:text-2xl">
                      {gameState.currentPrompt}
                    </div>
                  </div>
                  {!finished &&  (<div className="flex flex-wrap gap-2">
                    <div className={`rounded-full px-3 py-1 text-xs font-bold  tracking-[0.18em] ${timeLeft !== null && timeLeft <= 10 ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200' : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-200'}`}>
                      {finished ? '' : `Time left: ${timeLeft ?? timeLimit}s`}
                    </div>
                  </div>
                  )}
                </div>

                {!finished && timeLeft !== null && timeLimit > 0 && (
                  <div className="mt-4">
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <div
                        className={`h-full rounded-full transition-[width] duration-200 ${timeLeft <= 10 ? 'bg-rose-500' : 'bg-cyan-500'}`}
                        style={{ width: `${Math.max(0, Math.min(100, (timeLeft / timeLimit) * 100))}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
{!finished && (
              <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/85">
                <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Your guess</div>
                <div className="mt-3 flex flex-col gap-3">
                  <input
                    ref={inputRef}
                    value={guess}
                    onChange={(event) => setGuess(event.target.value)}
                    disabled={finished}
                    placeholder="Type an answer"
                    className="min-w-0 flex-1 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 disabled:cursor-not-allowed disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-cyan-400 dark:focus:ring-cyan-900"
                  />
                  <button
                    type="submit"
                    disabled={finished || !guess.trim()}
                    className="rounded-2xl bg-cyan-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:bg-slate-400 dark:bg-cyan-500 dark:text-slate-950 dark:hover:bg-cyan-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                  >
                    Submit answer
                  </button>
                  {!finished && (
                    <button
                      type="button"
                      onClick={handleGiveUpRequest}
                      disabled={!!giveUpRequest}
                      className="rounded-2xl bg-rose-500 px-5 py-3 text-sm font-bold text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:bg-slate-400 dark:hover:bg-rose-300 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                    >
                      Give up
                    </button>
                  )}
                </div>
                

                {feedback && (
                  <div className={`mt-3 rounded-2xl px-4 py-3 text-sm font-medium ${feedback.tone === 'success' ? 'border border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200' : feedback.tone === 'warn' ? 'border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/60 dark:text-amber-200' : 'border border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-700 dark:bg-rose-950/60 dark:text-rose-200'}`}>
                    {feedback.text}
                  </div>
                )}

                {giveUpRequest && !finished && (
                  <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
                    <div className="font-semibold">
                      {requestedByMe ? 'Waiting for the other players to respond to your give up request.' : `${requesterName} wants to end the round early.`}
                    </div>
                    <div className="mt-1">
                      If every player agrees, the round will end now and every answer will be revealed.
                    </div>
                    <div className="mt-2 text-xs opacity-80">
                      {Object.keys(giveUpRequest.approvals || {}).length} / {players.length} players have agreed.
                    </div>
                    {!requestedByMe && !myApprovedGiveUp && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleGiveUpResponse(true)}
                          className="rounded-xl bg-emerald-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-400"
                        >
                          End round
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGiveUpResponse(false)}
                          className="rounded-xl bg-slate-200 px-3 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                        >
                          Keep playing
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {gameState.lastGiveUpMessage && !giveUpRequest && !finished && (
                  <div className="mt-3 rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {gameState.lastGiveUpMessage}
                  </div>
                )}
              </form>
              )}

              <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/85">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Board</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">{visibleAnswerCount} of {totalAnswers} revealed</div>
                </div>
                <div className="mt-4 flex flex-col gap-3">
                  {Array.from({ length: totalAnswers }, (_, index) => {
                    const answer = revealedAnswers[index];
                    const isScratching = !!scratchingAnswers[index];
                    const showScratchOverlay = !answer || isScratching;
                    return (
                      <div
                        key={`answer-slot-${index}`}
                        className={`relative flex items-center justify-between rounded-2xl border px-4 py-3 min-h-[2.2rem] overflow-hidden transition-all duration-500 ${answer ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/50' : 'border-dashed border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/60'}`}
                      >
                        {showScratchOverlay && (
                          <div
                            className="absolute inset-0 z-10 pointer-events-none"
                            style={answer && isScratching ? { animation: 'surveysaysScratchReveal 850ms ease-out forwards' } : undefined}
                          />
                        )}
                        {showScratchOverlay && (
                          <div
                            className="absolute inset-0 z-10 pointer-events-none bg-[repeating-linear-gradient(135deg,_#cbd5e1_0px,_#cbd5e1_8px,_#f8fafc_8px,_#f8fafc_16px)] dark:bg-[repeating-linear-gradient(135deg,_#334155_0px,_#334155_8px,_#0f172a_8px,_#0f172a_16px)]"
                            style={answer && isScratching ? { animation: 'surveysaysScratchReveal 850ms ease-out forwards' } : undefined}
                          />
                        )}
                        <div className={`text-base font-semibold transition-colors duration-500 ${answer ? 'text-slate-900 dark:text-white' : 'text-slate-900 dark:text-slate-100'}`}
                          style={{ position: 'relative', zIndex: 20 }}>
                          {answer ? answer.text : `Answer ${index + 1}`}
                        </div>
                        {answer ? (
                          <div className="shrink-0 rounded-full bg-slate-900 px-2.5 py-1 text-xs font-bold text-white dark:bg-slate-100 dark:text-slate-900" style={{ position: 'relative', zIndex: 20 }}>{answer.value}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/85 mt-5">
                <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Player progress</div>
                <div className="mt-4 space-y-3">
                  {playerRows.map((row) => {
                    const pct = totalAnswers > 0 ? Math.round((row.foundIndexes.length / totalAnswers) * 100) : 0;
                    const foundLabels = row.foundIndexes.map((index) => {
                      const answer = row.isMe || shouldShowSharedAnswers ? revealedAnswers[index] : null;
                      return answer ? `${answer.text} (${answer.value})` : `Answer ${index + 1}`;
                    });
                    return (
                      <div
                        key={row.id}
                        className={`rounded-2xl border px-4 py-3 ${row.isWinner ? 'border-emerald-400 bg-emerald-100/80 dark:border-emerald-700 dark:bg-emerald-950/50' : row.isMe ? 'border-cyan-400 bg-cyan-50 dark:border-cyan-700 dark:bg-cyan-950/40' : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/70'}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold text-slate-900 dark:text-slate-100">{row.username}{row.isMe ? ' (You)' : ''}</div>
                          <div className="text-right">
                            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{row.foundIndexes.length}/{totalAnswers}</div>
                            <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{row.score} pts</div>
                          </div>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                          <div className={`h-full rounded-full ${row.isWinner ? 'bg-emerald-500' : 'bg-cyan-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {foundLabels.length > 0 ? foundLabels.map((label) => (
                            <span key={`${row.id}-${label}`} className="rounded-full bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
                              {label}
                            </span>
                          )) : (
                            <span className="text-xs text-slate-500 dark:text-slate-400">No answers found yet</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SurveySaysGame.Guide = function SurveySaysGuide() {
  return (
    <div className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
      <div className="font-bold">How to Play Survey Says</div>
      <div>Everyone sees the same prompt and races to guess the listed top answers before time runs out.</div>
      <div>In First to find all, each player only sees the answers they personally found until the round ends.</div>
      <div>In First to find each answer, every claimed answer is revealed to everyone and can only be scored once.</div>
      <div>Each revealed answer shows its value, and player totals are tracked on the progress board.</div>
      <div>You can request to give up early, and the round ends if every player agrees.</div>
    </div>
  );
};

export default SurveySaysGame;